import { faMagnifyingGlass, faPenLine, faUserGroup, faUserPlus, faXmark } from '@awesome.me/kit-a1e2571845/icons/classic/regular'
import { faBan, faCheck, faCircleXmark } from '@awesome.me/kit-a1e2571845/icons/classic/solid'
import { css } from '@emotion/css'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import MuiIconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { SxProps } from '@mui/system/styleFunctionSx'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { DataGrid, Icon } from '@xweather/theme'
import { debounce } from 'es-toolkit'
import Fuse from 'fuse.js'
import i18next from 'i18next'
import { enqueueSnackbar } from 'notistack'
import { type ComponentProps, Fragment, type MouseEventHandler, memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { create } from 'zustand'
import { combine } from 'zustand/middleware'
import type { UserRoleEntity } from '../../../api/user-manager.ts'
import { LinkButton, Menu } from '../../../components/generic.tsx'
import { I18n } from '../../../components/I18n.tsx'
import { IconButton, LinkIconButton } from '../../../components/IconButton.tsx'
import { WithoutNavigation } from '../../../components/Layouts.tsx'
import { useConfirmDialog } from '../../../components/useConfirmDialog.tsx'
import { useMenu } from '../../../components/useMenu.ts'
import { useSubscription } from '../../../data/service.ts'
import { userListData } from '../../../data/userLists.ts'
import { currentUserData } from '../../../data/userProfile.ts'
import {
	type InsightUser,
	roleData,
	useDeleteUser,
	useDisableMFA,
	useEnableMFA,
	useResendEmailVerification,
	useResetPassword,
	userData,
	useUpdateUser,
} from '../../../data/users.ts'
import { useFormatDate } from '../../../features/preferences/useFormatDate.tsx'
import { useFlags } from '../../../launchdarkly.ts'
import { canEditRole, RoleChip, RoleIcon } from './-roleUtils.tsx'

export const Route = createFileRoute('/_app/users/')({
	validateSearch: z.object({ list: z.string().optional() }),
	loaderDeps: ({ search: { list } }) => ({ list }),
	loader: async ({ context, deps: { list } }) => {
		const [userLists] = await Promise.all([userListData.ensure(context), userData.ensure(context), currentUserData.ensure()])
		const selectedUserList = userLists.find((userList) => userList.id === list)
		return { selectedUserList }
	},
	component: Users,
})

function Users() {
	const { selectedUserList } = Route.useLoaderData()
	const navigate = useNavigate()
	const { data: users } = userData.use()
	const { data: subscription } = useSubscription()
	const canAddUsers = users.filter((u) => u.isUiUser).length < subscription.maxUsers
	return (
		<WithoutNavigation
			onBack={() => navigate({ to: '..' })}
			topBar={
				<Stack full sx={{ align: 'center' }}>
					<Icon icon={faUserGroup} />
					<UserListSelect sx={{ w: 420 }} />
					<Divider orientation='vertical' variant='middle' flexItem />
					<SearchField sx={{ maxW: 640, flexGrow: 1 }} />
					<RoleFilterField sx={{ minW: 160 }} />
					<Spacer />
					<LinkButton
						variant='text'
						to='/users/list/{-$id}'
						params={{ id: selectedUserList?.id }}
						disabled={!selectedUserList}
						startIcon={<Icon icon={faPenLine} />}
					>
						<I18n i18nKey={($) => $.users.list.edit} />
					</LinkButton>
					<LinkButton to='/users/list/{-$id}'>
						<I18n i18nKey={($) => $.users.list.add} />
					</LinkButton>
					<Tooltip title={canAddUsers ? '' : <I18n i18nKey={($) => $.users.limitReached} values={{ maxUsers: subscription.maxUsers }} />}>
						<Box>
							<LinkButton to='/users/add' disabled={!canAddUsers} startIcon={<Icon icon={faUserPlus} />}>
								<I18n i18nKey={($) => $.users.add} />
							</LinkButton>
						</Box>
					</Tooltip>
				</Stack>
			}
		>
			<UserGrid />
		</WithoutNavigation>
	)
}

type RoleFilter = UserRoleEntity['name'] | 'all'

const useGridState = create(
	combine({ search: '', roleFilter: 'all' as RoleFilter, focusUser: undefined as InsightUser | undefined }, (set) => ({
		setSearch: (search: string) => set({ search }),
		setRoleFilter: (roleFilter: RoleFilter) => set({ roleFilter }),
		setFocusUser: (user: InsightUser | undefined) => set({ focusUser: user }),
	})),
)

const UserGrid = () => {
	const { selectedUserList } = Route.useLoaderData()
	const { data: users } = userData.use()
	const search = useGridState((s) => s.search)
	const roleFilter = useGridState((s) => s.roleFilter)

	const filteredUsers = useMemo(() => {
		const uiUsers = users.filter(
			(u) =>
				u.isUiUser &&
				(!selectedUserList || selectedUserList.userIds.includes(u.id)) &&
				(roleFilter === 'all' || u.role.name === roleFilter),
		)
		return search
			? new Fuse(uiUsers, { keys: ['fullName', ['email', 'email']], threshold: 0.3 }).search(search).map((r) => r.item)
			: uiUsers
	}, [users, selectedUserList, search, roleFilter])

	return (
		<DataGrid
			data={filteredUsers}
			columns={[
				{
					accessorKey: 'fullName',
					header: () => <I18n i18nKey={($) => $.users.name} />,
					cell: ({ row }) => <NameCell user={row.original} />,
					minSize: 300,
					meta: { grow: 1 },
				},
				{
					id: 'role',
					accessorFn: (row) => i18next.t(($) => $.users.roles[row.role.name]),
					header: () => <I18n i18nKey={($) => $.users.role} />,
					cell: ({ row }) => <RoleCell user={row.original} />,
				},
				{
					id: 'email',
					accessorFn: (row) => row.email.email,
					header: () => <I18n i18nKey={($) => $.users.email} />,
					cell: ({ row }) => <EmailCell user={row.original} />,
					meta: { grow: 1 },
				},
				{
					id: 'phone',
					header: () => <I18n i18nKey={($) => $.users.phone} />,
					cell: ({ row }) => <PhoneCell user={row.original} />,
				},
				{
					id: 'status',
					header: () => <I18n i18nKey={($) => $.users.status} />,
					cell: ({ row }) => <StatusCell user={row.original} />,
					size: 260,
				},
				{
					accessorKey: 'hasMfa',
					header: () => <I18n i18nKey={($) => $.users.mfa} />,
					cell: ({ row }) => <MfaCell user={row.original} />,
					size: 80,
				},
				{
					accessorKey: 'createdAt',
					header: () => <I18n i18nKey={($) => $.users.created} />,
					cell: ({ row }) => <CreatedCell user={row.original} />,
				},
			]}
			slotProps={{
				tableContainer: { sx: { px: 2 } },
				bodyRow: { sx: { [`:hover .${showOnHover}`]: { visibility: 'visible' } } },
			}}
		/>
	)
}

const Spacer = () => <Box sx={{ flexGrow: 1 }} />

const UserListSelect = ({ sx }: { sx?: SxProps }) => {
	const { selectedUserList } = Route.useLoaderData()
	const { data: users } = userData.use()
	const { data: userLists } = userListData.use()
	const navigate = useNavigate()
	return (
		<TextField
			select
			size='large'
			variant='standard'
			value={selectedUserList?.id ?? 'all'}
			sx={sx}
			onChange={({ target: { value } }) =>
				navigate({ from: '/users/', search: (prev) => ({ ...prev, list: value === 'all' ? undefined : value }) })
			}
		>
			<MenuItem value='all'>
				<Stack full>
					<I18n i18nKey={($) => $.users.allUsers} />
					<Spacer />
					<Chip label={<I18n i18nKey={($) => $.users.userCount} values={{ count: users.filter((u) => u.isUiUser).length }} />} />
				</Stack>
			</MenuItem>

			{userLists
				.toSorted((a, b) => {
					if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1
					return a.name.localeCompare(b.name)
				})
				.map((list) => (
					<MenuItem key={list.id} value={list.id}>
						<Stack full>
							<Typography noWrap>{list.name}</Typography>
							<Spacer />
							{list.isDefault && <Chip variant='filled' label={<I18n i18nKey={($) => $.users.list.default} />} />}
							<Chip label={<I18n i18nKey={($) => $.users.userCount} values={{ count: list.userIds.length }} />} />
						</Stack>
					</MenuItem>
				))}
		</TextField>
	)
}

const SearchField = ({ sx }: { sx?: SxProps }) => {
	const { t } = useTranslation()
	const [value, setValue] = useState('')
	const search = useGridState((s) => s.search)
	const setSearch = useGridState((s) => s.setSearch)
	const debouncedSearch = useMemo(() => debounce(setSearch, 200), [setSearch])

	useEffect(() => setValue(search), [search])
	useEffect(() => debouncedSearch(value), [debouncedSearch, value])

	return (
		<TextFieldDarkBg
			placeholder={t(($) => $.searchPlaceholder)}
			sx={sx}
			value={value}
			onChange={(e) => setValue(e.target.value)}
			slotProps={{
				input: {
					startAdornment: (
						<InputAdornment position='start'>
							<Icon icon={faMagnifyingGlass} />
						</InputAdornment>
					),
					endAdornment: (
						<InputAdornment position='end'>
							<MuiIconButton edge='end' onClick={() => setSearch('')}>
								<Icon icon={faCircleXmark} />
							</MuiIconButton>
						</InputAdornment>
					),
				},
			}}
		/>
	)
}

const RoleFilterField = ({ sx }: { sx?: SxProps }) => {
	const roleFilter = useGridState((s) => s.roleFilter)
	const setRoleFilter = useGridState((s) => s.setRoleFilter)
	const { data: roles } = roleData.use()
	return (
		<TextFieldDarkBg select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as RoleFilter)} sx={sx}>
			<MenuItem value='all'>
				<I18n i18nKey={($) => $.users.list.allRoles} />
			</MenuItem>
			{Object.values(roles ?? {}).map(({ name }) => (
				<MenuItem key={name} value={name}>
					<I18n i18nKey={($) => $.users.roles[name]} />
				</MenuItem>
			))}
		</TextFieldDarkBg>
	)
}

const TextFieldDarkBg = ({ sx, ...props }: ComponentProps<typeof TextField>) => (
	<TextField
		sx={{
			'& .MuiInputBase-root': {
				'&, :hover, :focus': { bgcolor: (t) => (t.palette.mode === 'dark' ? t.palette.background.paper3 : undefined) },
			},
			...sx,
		}}
		{...props}
	/>
)

const Cell = ({ sx, ...props }: ComponentProps<typeof Stack>) => <Stack full sx={{ align: 'center', ...sx }} {...props} />

const EmailCell = memo(({ user }: { user: InsightUser }) => (
	<Cell>
		<Typography noWrap>{user.email.email}</Typography>
	</Cell>
))

const PhoneCell = memo(({ user }: { user: InsightUser }) => <Cell>{user.phone.phone}</Cell>)

const MfaCell = memo(({ user }: { user: InsightUser }) => (
	<Cell>
		<StatusChip error={!user.hasMfa} />
	</Cell>
))

const RoleCell = memo(({ user }: { user: InsightUser }) => (
	<Cell>
		<RoleChip role={user.role} />
	</Cell>
))

const CreatedCell = memo(({ user }: { user: InsightUser }) => {
	const formatDate = useFormatDate()
	return <Cell>{formatDate(user.createdAt, 'DD.MM.YYYY')}</Cell>
})

const NameCell = memo(({ user }: { user: InsightUser }) => {
	const { t } = useTranslation()
	const isFocused = useGridState((s) => s.focusUser === user)
	const { data: currentUser } = currentUserData.use()
	const isCurrentUser = currentUser.id === user.id
	return (
		<Cell>
			<RoleIcon role={user.role} color='textSecondary' />
			<Typography noWrap>{user.fullName}</Typography>
			<Spacer />
			<Stack>
				{(isCurrentUser || canEditRole(currentUser, user.role)) && (
					<LinkIconButton
						className={showOnHover}
						sx={isFocused ? { visibility: 'visible' } : {}}
						icon='PenLine'
						label={t(($) => $.edit)}
						color='primary'
						size='small'
						to='/users/$id'
						params={{ id: user.id }}
					/>
				)}
				<UserActionsMenu user={user} isFocused={isFocused} />
			</Stack>
		</Cell>
	)
})
const showOnHover = css({ visibility: 'hidden' })

const UserActionsMenu = ({ user, isFocused }: { user: InsightUser; isFocused: boolean }) => {
	const { t } = useTranslation()
	const { openMenu, closeMenu, menuProps } = useMenu()
	const setFocusUser = useGridState((s) => s.setFocusUser)
	const { data: currentUser } = currentUserData.use()
	if (!canEditRole(currentUser, user.role)) return null

	const isCurrentUser = currentUser.id === user.id
	if (user.isSsoUser && isCurrentUser) return null

	const closeMenuUnfocus = () => {
		closeMenu()
		setFocusUser(undefined)
	}
	const openMenuFocus: MouseEventHandler<HTMLButtonElement> = (e) => {
		openMenu(e)
		setFocusUser(user)
	}

	return (
		<>
			<IconButton
				className={showOnHover}
				sx={isFocused ? { visibility: 'visible' } : {}}
				icon='EllipsisV'
				size='small'
				label={t(($) => $.users.actions)}
				onClick={openMenuFocus}
			/>
			<Menu {...menuProps} onClose={closeMenuUnfocus}>
				<ResetPassword user={user} onSuccess={closeMenuUnfocus} />
				<ResendEmailVerification user={user} onSuccess={closeMenuUnfocus} />
				<EnableMFA user={user} onSuccess={closeMenuUnfocus} />
				<DisableMFA user={user} onSuccess={closeMenuUnfocus} />
				<BlockUser user={user} onSuccess={closeMenuUnfocus} />
				<UnblockUser user={user} onSuccess={closeMenuUnfocus} />
				<DeleteUser user={user} onSuccess={closeMenuUnfocus} />
			</Menu>
		</>
	)
}

const ResetPassword = ({ user, onSuccess }: { user: InsightUser; onSuccess: () => void }) => {
	const { mutateAsync: resetPassword, isPending } = useResetPassword()
	if (user.isSsoUser) return null

	const onClick = () =>
		resetPassword(user.id, {
			onSuccess: () => {
				enqueueSnackbar(<I18n i18nKey={($) => $.users.passwordResetSuccess} />, { variant: 'success' })
				onSuccess()
			},
			onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.users.passwordResetError} />, { variant: 'error' }),
		})
	return (
		<MenuItem onClick={onClick} disabled={isPending}>
			<I18n i18nKey={($) => $.users.resetPassword} />
		</MenuItem>
	)
}

const ResendEmailVerification = ({ user, onSuccess }: { user: InsightUser; onSuccess: () => void }) => {
	const { mutateAsync: resendEmailVerification, isPending } = useResendEmailVerification()
	const { intercomUserOnboarding } = useFlags()
	if (intercomUserOnboarding || user.isSsoUser || user.email.verified) return null

	const onClick = () =>
		resendEmailVerification(user.id, {
			onSuccess: () => {
				enqueueSnackbar(<I18n i18nKey={($) => $.preferences.emailVerification.sent} />, { variant: 'success' })
				onSuccess()
			},
			onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.preferences.emailVerification.failure} />, { variant: 'error' }),
		})
	return (
		<MenuItem onClick={onClick} disabled={isPending}>
			<I18n i18nKey={($) => $.users.resendEmailVerification} />
		</MenuItem>
	)
}

const EnableMFA = ({ user, onSuccess }: { user: InsightUser; onSuccess: () => void }) => {
	const { mutateAsync: enableMFA, isPending } = useEnableMFA()
	if (user.isSsoUser || !user.email.verified || user.hasMfa) return null

	const onClick = () =>
		enableMFA(user.id, {
			onSuccess: () => {
				enqueueSnackbar(<I18n i18nKey={($) => $.users.enableMFASuccess} />, { variant: 'success' })
				onSuccess()
			},
			onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.users.enableMFAError} />, { variant: 'error' }),
		})
	return (
		<MenuItem onClick={onClick} disabled={isPending}>
			<I18n i18nKey={($) => $.users.enableMFA} />
		</MenuItem>
	)
}

const DisableMFA = ({ user, onSuccess }: { user: InsightUser; onSuccess: () => void }) => {
	const { mutateAsync: disableMFA, isPending } = useDisableMFA()
	if (user.isSsoUser || !user.email.verified || !user.hasMfa) return null

	const onClick = () =>
		disableMFA(user.id, {
			onSuccess: () => {
				enqueueSnackbar(<I18n i18nKey={($) => $.users.disableMFASuccess} />, { variant: 'success' })
				onSuccess()
			},
			onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.users.disableMFAError} />, { variant: 'error' }),
		})
	return (
		<MenuItem onClick={onClick} disabled={isPending}>
			<I18n i18nKey={($) => $.users.disableMFA} />
		</MenuItem>
	)
}

const BlockUser = ({ user, onSuccess }: { user: InsightUser; onSuccess: () => void }) => {
	const { mutateAsync: updateUser, isPending } = useUpdateUser()
	const { data: currentUser } = currentUserData.use()
	if (currentUser.id === user.id || user.isBlocked) return null

	const onClick = () =>
		updateUser(
			{ id: user.id, body: { blocked: true } },
			{
				onSuccess: () => {
					enqueueSnackbar(<I18n i18nKey={($) => $.users.blockSuccess} />, { variant: 'success' })
					onSuccess()
				},
				onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.users.blockFailure} />, { variant: 'error' }),
			},
		)
	return (
		<MenuItem color='error' onClick={onClick} disabled={isPending}>
			<I18n i18nKey={($) => $.users.blockUser} />
		</MenuItem>
	)
}

const UnblockUser = ({ user, onSuccess }: { user: InsightUser; onSuccess: () => void }) => {
	const { mutateAsync: updateUser, isPending } = useUpdateUser()
	const { data: currentUser } = currentUserData.use()
	if (currentUser.id === user.id || !user.isBlocked) return null

	const onClick = () =>
		updateUser(
			{ id: user.id, body: { blocked: false } },
			{
				onSuccess: () => {
					enqueueSnackbar(<I18n i18nKey={($) => $.users.unblockSuccess} />, { variant: 'success' })
					onSuccess()
				},
				onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.users.unblockFailure} />, { variant: 'error' }),
			},
		)
	return (
		<MenuItem color='error' onClick={onClick} disabled={isPending}>
			<I18n i18nKey={($) => $.users.unblockUser} />
		</MenuItem>
	)
}

const DeleteUser = ({ user, onSuccess }: { user: InsightUser; onSuccess: () => void }) => {
	const [deleting, setDeleting] = useState<InsightUser>()
	const { mutateAsync: deleteUser } = useDeleteUser()
	const { Dialog, openDialog } = useConfirmDialog({
		title: <I18n i18nKey={($) => $.users.deleteUser} />,
		content: <I18n i18nKey={($) => $.users.deleteUserConfirmation} values={{ user: deleting }} />,
		actionName: <I18n i18nKey={($) => $.delete} />,
		color: 'error',
		onConfirm: async () => {
			if (deleting)
				await deleteUser(deleting.id, {
					onSuccess: () => {
						enqueueSnackbar(<I18n i18nKey={($) => $.users.deleteSuccess} />, { variant: 'success' })
						onSuccess()
					},
					onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.users.deleteFailure} />, { variant: 'error' }),
				})
		},
	})
	const { data: currentUser } = currentUserData.use()
	if (currentUser.id === user.id) return null

	const onClick = () => {
		setDeleting(user)
		openDialog()
	}
	return [
		<MenuItem key='menuitem' color='error' onClick={onClick}>
			<I18n i18nKey={($) => $.users.deleteUser} />
		</MenuItem>,
		<Fragment key='dialog'>{Dialog}</Fragment>,
	]
}

const StatusCell = memo(({ user }: { user: InsightUser }) => (
	<Cell>
		{user.isBlocked && <Chip color='warning' icon={<Icon icon={faBan} />} label={<I18n i18nKey={($) => $.users.blocked} />} />}
		{!user.isBlocked && (
			<>
				<StatusChip
					error={!user.email.verified}
					label={<I18n i18nKey={($) => (user.email.verified ? $.users.email : $.users.emailUnverified)} />}
				/>
				{user.phone.phone && <StatusChip error={!user.phone.verified} label={<I18n i18nKey={($) => $.users.phone} />} />}
			</>
		)}
	</Cell>
))

const StatusChip = ({ error, ...props }: { error?: boolean } & ComponentProps<typeof Chip>) => (
	<Chip color={error ? 'error' : 'default'} icon={<Icon icon={error ? faXmark : faCheck} />} {...props} />
)
